import React from 'react';
import MediaQuery from 'react-responsive';

export default class EstimatedDelivery extends React.PureComponent {
  constructor(props) {
    super(props);
    // Find all blocks that match the position
    this.blocks = props.blocks.filter(block => 
      (block.type === 'estimated_delivery' || block.type === 'estimated_delivery_2') && 
      block.settings.position === props.position
    );
    
    this.calculateDateRange = (startDate, minDays, maxDays, holidays, expeditedDays = null) => {
      let currentDay = new Date(startDate);
      let count = 0;

      const isNonWorkingDay = (date) => {
        const day = date.getDay();
        if (day === 0 || day === 6) return true;
        const formattedDate = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
        return holidays.includes(formattedDate);
      };

      // Calculate expedited date if provided
      let expeditedDate = null;
      if (expeditedDays !== null) {
        currentDay = new Date(startDate);
        count = 0;
        while (count < expeditedDays) {
          currentDay.setDate(currentDay.getDate() + 1);
          if (!isNonWorkingDay(currentDay)) count++;
        }
        expeditedDate = new Date(currentDay);
      }

      // Calculate min date
      currentDay = new Date(startDate);
      count = 0;
      while (count < minDays) {
        currentDay.setDate(currentDay.getDate() + 1);
        if (!isNonWorkingDay(currentDay)) count++;
      }
      const minDeliveryDate = new Date(currentDay);

      // Calculate max date
      currentDay = new Date(startDate);
      count = 0;
      while (count < maxDays) {
        currentDay.setDate(currentDay.getDate() + 1);
        if (!isNonWorkingDay(currentDay)) count++;
      }
      const maxDeliveryDate = new Date(currentDay);

      const options = { weekday: 'short', month: 'short', day: 'numeric' };
      return {
        minDate: minDeliveryDate.toLocaleDateString('en-US', options),
        maxDate: maxDeliveryDate.toLocaleDateString('en-US', options),
        expDate: expeditedDate ? expeditedDate.toLocaleDateString('en-US', options) : null
      };
    }

    this.calDate = (minDays, maxDays, cutoffTime, holidays = [], expeditedDays = null) => {
      // Get current time
      const now = new Date();
      
      // Check if today is a weekend
      const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      
      // If it's a weekend, add a day to account for the weekend
      if (isWeekend) {
        minDays += 1;
        maxDays += 1;
        if (expeditedDays !== null) {
          expeditedDays += 1;
        }
      }
      
      // If we have a cutoff time and it's not a weekend, check if we're past it
      if (cutoffTime && !isWeekend) {
        // Create cutoff time for today
        const [hours, minutes] = cutoffTime.split(':').map(Number);
        const cutoff = new Date(now);
        cutoff.setHours(hours, minutes, 0, 0);
        
        // If we're past cutoff, add a day to all calculations
        if (now > cutoff) {
          minDays += 1;
          maxDays += 1;
          if (expeditedDays !== null) {
            expeditedDays += 1;
          }
        }
      }
      
      return this.calculateDateRange(now, minDays, maxDays, holidays, expeditedDays);
    }

    // Add modal popup functionality
    this.handleModalClick = this.handleModalClick.bind(this);
  }

  handleModalClick(e) {
    // Only trigger if the clicked element or its parent is a link with href="#modal-popup"
    const link = e.target.closest('a[href="#modal-popup"]');
    if (link) {
      e.preventDefault();
      const popup = document.querySelector('.pxs-announcement-popup-main');
      if (popup) {
        popup.classList.toggle('visible');
      }
    }
  }

  renderBlock(block) {
    const settings = block.settings;
    const holidays = settings.holidays ? settings.holidays.split('\n').map(date => date.trim()) : [];

    const mediaAttr = {};
    if (settings.desktop === 'hide' && settings.mobile === 'hide') {
      return null;
    } else if (settings.desktop === 'hide') {
      mediaAttr.maxWidth = 719;
    } else if (settings.mobile === 'hide') {
      mediaAttr.minWidth = 720;
    } else {
      mediaAttr.minWidth = 0;
    }

    const expeditedDays = block.type === 'estimated_delivery_2' ? settings.expedited_days : null;
    const dateRange = this.calDate(settings.min_days, settings.max_days, settings.cutoff, holidays, expeditedDays);
    const {minDate, maxDate, expDate} = dateRange;

    // Replace date placeholders with temporary markers to preserve them
    const tempMinDate = '___MIN_DATE___';
    const tempMaxDate = '___MAX_DATE___';
    const tempExpDate = '___EXP_DATE___';

    let formattedText = settings.preceding_text
      .replace("{ min_date }", tempMinDate)
      .replace("{ max_date }", tempMaxDate);
    
    if (expDate) {
      formattedText = formattedText.replace("{ exp_date }", tempExpDate);
    }

    // Replace temporary markers with actual dates
    formattedText = formattedText
      .replace(tempMinDate, minDate)
      .replace(tempMaxDate, maxDate)
      .replace(tempExpDate, expDate || '');

    let secondaryText = null;
    if (block.type === 'estimated_delivery_2' && settings.secondary_text) {
      secondaryText = settings.secondary_text
        .replace("{ min_date }", tempMinDate)
        .replace("{ max_date }", tempMaxDate);
      
      if (expDate) {
        secondaryText = secondaryText.replace("{ exp_date }", tempExpDate);
      }

      // Replace temporary markers with actual dates
      secondaryText = secondaryText
        .replace(tempMinDate, minDate)
        .replace(tempMaxDate, maxDate)
        .replace(tempExpDate, expDate || '');
    }

    return (
      <MediaQuery {...mediaAttr} key={block.type}>
        <div className={`est-delivery-main ${block.type === 'estimated_delivery' ? 'est-delivery-type-1' : 'est-delivery-type-2'}`} style={{ textAlign: settings['text-alignment'] }}>
          <div className="est-delivery-primary" dangerouslySetInnerHTML={{ __html: formattedText }} onClick={this.handleModalClick} />
          {secondaryText && <div className="est-delivery-secondary" dangerouslySetInnerHTML={{ __html: secondaryText }} onClick={this.handleModalClick} />}
          {block.type === 'estimated_delivery_2' && settings.tertiary_text && (
            <div className="est-delivery-tertiary" dangerouslySetInnerHTML={{ __html: settings.tertiary_text }} onClick={this.handleModalClick} />
          )}
        </div>
      </MediaQuery>
    );
  }

  render() {
    if (!this.blocks || this.blocks.length === 0) {
      console.log('No blocks found for position:', this.props.position);
      return null;
    }

    return (
      <React.Fragment>
        {this.blocks.map(block => this.renderBlock(block))}
      </React.Fragment>
    );
  }
}